import AsyncStorage from '@react-native-async-storage/async-storage';
import Constants from 'expo-constants';
import * as Linking from 'expo-linking';
import * as Application from 'expo-application';
import { Alert, AppState, Platform } from 'react-native';
import { useUserStore } from '@/state/userStore';
import { useRatingPromptStore } from '@/state/ratingPromptStore';
import { networkMonitor } from '@/services/networkMonitor';
import { firebaseApp } from '@/lib/firebase';
import { logAnalyticsEvent } from '@/services/analyticsLogger';
import { openSupportMailbox } from '@/utils/supportContact';
import { acquirePromptLock, releasePromptLock } from '@/services/promptCoordinator';
import { showBridgeToast } from '@/services/toastBridge';

const STORAGE_KEY = '@tripvio_rating_prompt_state_v1';
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MS_PER_YEAR = 365 * MS_PER_DAY;
const DEBUG_RATING_PROMPT =
  __DEV__ || process.env.EXPO_PUBLIC_RATING_PROMPT_DEBUG === 'true';

const DEFAULT_POLICY = {
  enabled: process.env.EXPO_PUBLIC_RATING_PROMPT_ENABLED !== 'false',
  cooldownDays: 45,
  deferDays: 14,
  maxPerYear: 2,
};

export type RatingPromptTrigger =
  | 'trip_creation_success'
  | 'email_sync_import_success'
  | 'trip_completed_milestone'
  | 'yearly_summary_share_success'
  | 'template_rating_success'
  | 'trip_recap_share_success'
  | 'trip_dossier_export_success';

export type RatingPromptAttemptOptions = {
  trigger: RatingPromptTrigger;
  tripId?: string;
  source?: string;
  bookingsCount?: number;
  deferMs?: number;
  hasBlockingUi?: boolean;
};

type RatingPromptState = {
  optedOut: boolean;
  completedAt: number | null;
  lastPromptAt: number | null;
  deferUntil: number | null;
  promptTimestamps: number[];
  completedTripIds: string[];
};

type RatingPromptPolicy = {
  enabled: boolean;
  cooldownMs: number;
  deferMs: number;
  maxPerYear: number;
};

type EligibilityInput = {
  now: number;
  trigger: RatingPromptTrigger;
  tripId?: string;
  state: RatingPromptState;
  policy: RatingPromptPolicy;
};

type EligibilityResult =
  | { eligible: true }
  | {
      eligible: false;
      reason:
        | 'opted_out'
        | 'completed'
        | 'deferred'
        | 'cooldown'
        | 'annual_cap'
        | 'trip_already_prompted';
    };

const EMPTY_STATE: RatingPromptState = {
  optedOut: false,
  completedAt: null,
  lastPromptAt: null,
  deferUntil: null,
  promptTimestamps: [],
  completedTripIds: [],
};

let policyCache: RatingPromptPolicy | null = null;
let policyFetchStarted = false;
let attemptInFlight = false;
let storeReviewModulePromise: Promise<StoreReviewModule | null> | null = null;

const debugRatingPrompt = (message: string, details?: Record<string, unknown>) => {
  if (!DEBUG_RATING_PROMPT) return;
  const detailText = details
    ? Object.entries(details)
        .map(([key, value]) => `${key}=${String(value)}`)
        .join(', ')
    : undefined;
  showBridgeToast({
    message: `Review debug: ${message}`,
    type: 'info',
    duration: 3200,
    details: detailText,
  });
  if (details) {
    console.info(`[ratingPrompt] ${message}`, details);
    return;
  }
  console.info(`[ratingPrompt] ${message}`);
};

type StoreReviewModule = {
  hasAction: () => Promise<boolean>;
  requestReview: () => Promise<void>;
  storeUrl: () => string | null;
};

const toPolicy = (raw: {
  enabled: boolean;
  cooldownDays: number;
  deferDays: number;
  maxPerYear: number;
}): RatingPromptPolicy => ({
  enabled: raw.enabled,
  cooldownMs: Math.max(1, Math.floor(raw.cooldownDays)) * MS_PER_DAY,
  deferMs: Math.max(1, Math.floor(raw.deferDays)) * MS_PER_DAY,
  maxPerYear: Math.max(1, Math.floor(raw.maxPerYear)),
});

const sanitizeState = (value: Partial<RatingPromptState> | null | undefined): RatingPromptState => {
  if (!value || typeof value !== 'object') return EMPTY_STATE;
  return {
    optedOut: value.optedOut === true,
    completedAt: typeof value.completedAt === 'number' ? value.completedAt : null,
    lastPromptAt: typeof value.lastPromptAt === 'number' ? value.lastPromptAt : null,
    deferUntil: typeof value.deferUntil === 'number' ? value.deferUntil : null,
    promptTimestamps: Array.isArray(value.promptTimestamps)
      ? value.promptTimestamps.filter((t): t is number => typeof t === 'number')
      : [],
    completedTripIds: Array.isArray(value.completedTripIds)
      ? value.completedTripIds.filter((id): id is string => typeof id === 'string')
      : [],
  };
};

const getStoreListingUrl = (): string | null => {
  const iosUrl = process.env.EXPO_PUBLIC_IOS_APP_STORE_URL;
  const iosId = process.env.EXPO_PUBLIC_IOS_APP_STORE_ID;

  if (Platform.OS === 'ios') {
    if (iosUrl) return iosUrl;
    if (iosId) return `https://apps.apple.com/app/id${iosId}`;
    return null;
  }
  if (Platform.OS === 'android') {
    const androidUrl = process.env.EXPO_PUBLIC_ANDROID_PLAY_STORE_URL;
    if (androidUrl) return androidUrl;
    const appId =
      Application.applicationId ||
      Constants.expoConfig?.android?.package ||
      (Constants.expoConfig?.extra?.androidPackage as string | undefined);
    if (!appId) return null;
    const host = ['play', 'google', 'com'].join('.');
    return `https://${host}/store/apps/details?id=${appId}`;
  }
  return null;
};

const evaluateEligibility = ({ now, trigger, tripId, state, policy }: EligibilityInput): EligibilityResult => {
  if (state.optedOut) return { eligible: false, reason: 'opted_out' };
  if (state.completedAt != null) return { eligible: false, reason: 'completed' };
  if (state.deferUntil != null && now < state.deferUntil) return { eligible: false, reason: 'deferred' };
  if (state.lastPromptAt != null && now - state.lastPromptAt < policy.cooldownMs) {
    return { eligible: false, reason: 'cooldown' };
  }
  const promptsInLastYear = state.promptTimestamps.filter((ts) => now - ts < MS_PER_YEAR).length;
  if (promptsInLastYear >= policy.maxPerYear) return { eligible: false, reason: 'annual_cap' };
  if (trigger === 'trip_completed_milestone' && tripId && state.completedTripIds.includes(tripId)) {
    return { eligible: false, reason: 'trip_already_prompted' };
  }
  return { eligible: true };
};

const readState = async (): Promise<RatingPromptState> => {
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_STATE;
    return sanitizeState(JSON.parse(raw) as Partial<RatingPromptState>);
  } catch {
    return EMPTY_STATE;
  }
};

const writeState = async (next: RatingPromptState): Promise<void> => {
  try {
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore persistence failures
  }
};

const withPromptRecorded = (
  state: RatingPromptState,
  now: number,
  tripId?: string,
): RatingPromptState => {
  const promptTimestamps = [...state.promptTimestamps.filter((ts) => now - ts < MS_PER_YEAR), now];
  const completedTripIds =
    tripId && !state.completedTripIds.includes(tripId)
      ? [...state.completedTripIds.slice(-24), tripId]
      : state.completedTripIds;

  return {
    ...state,
    lastPromptAt: now,
    promptTimestamps,
    completedTripIds,
  };
};

const maybeInitRemotePolicy = async (): Promise<void> => {
  if (policyFetchStarted) return;
  policyFetchStarted = true;

  try {
    const { fetchAndActivate, getRemoteConfig, getValue } = await import('firebase/remote-config');
    const remoteConfig = getRemoteConfig(firebaseApp);
    remoteConfig.settings.minimumFetchIntervalMillis = 60 * 60 * 1000;
    await fetchAndActivate(remoteConfig);

    const enabledRaw = getValue(remoteConfig, 'rating_prompt_enabled').asString().trim().toLowerCase();
    const cooldownRaw = Number(getValue(remoteConfig, 'rating_prompt_cooldown_days').asNumber());
    const deferRaw = Number(getValue(remoteConfig, 'rating_prompt_defer_days').asNumber());
    const annualRaw = Number(getValue(remoteConfig, 'rating_prompt_max_per_year').asNumber());

    policyCache = toPolicy({
      enabled:
        enabledRaw === 'true'
          ? true
          : enabledRaw === 'false'
          ? false
          : policyCache?.enabled ?? DEFAULT_POLICY.enabled,
      cooldownDays: Number.isFinite(cooldownRaw) && cooldownRaw > 0 ? cooldownRaw : DEFAULT_POLICY.cooldownDays,
      deferDays: Number.isFinite(deferRaw) && deferRaw > 0 ? deferRaw : DEFAULT_POLICY.deferDays,
      maxPerYear: Number.isFinite(annualRaw) && annualRaw > 0 ? annualRaw : DEFAULT_POLICY.maxPerYear,
    });
  } catch {
    // Remote Config may be unavailable in local/dev contexts.
  }
};

const loadStoreReviewModule = async (): Promise<StoreReviewModule | null> => {
  if (!storeReviewModulePromise) {
    storeReviewModulePromise = import('expo-store-review')
      .then((module) => {
        if (
          typeof module.hasAction !== 'function' ||
          typeof module.requestReview !== 'function' ||
          typeof module.storeUrl !== 'function'
        ) {
          return null;
        }
        return module as StoreReviewModule;
      })
      .catch(() => null);
  }

  return storeReviewModulePromise;
};

const getPolicy = async (): Promise<RatingPromptPolicy> => {
  if (!policyCache) {
    policyCache = toPolicy(DEFAULT_POLICY);
  }
  await maybeInitRemotePolicy();
  return policyCache;
};

const openStoreListing = async (): Promise<boolean> => {
  const storeReview = await loadStoreReviewModule();
  const expoStoreUrl = storeReview?.storeUrl?.() ?? null;
  if (expoStoreUrl) {
    try {
      await Linking.openURL(expoStoreUrl);
      return true;
    } catch {
      // Continue to fallback URL resolution.
    }
  }

  const fallback = getStoreListingUrl();
  if (!fallback) return false;

  try {
    await Linking.openURL(fallback);
    return true;
  } catch {
    return false;
  }
};

const canAttemptPromptNow = (options?: RatingPromptAttemptOptions): boolean => {
  if (Platform.OS === 'web') return false;
  if (options?.hasBlockingUi) return false;
  if (AppState.currentState !== 'active') return false;
  if (networkMonitor.shouldUseOfflineDataFallback()) return false;

  const store = useRatingPromptStore.getState();
  if (store.isPromptVisible || store.sessionAttempted) return false;

  const userState = useUserStore.getState();
  if (!userState.authReady || !userState.firebaseUser?.uid || !userState.hasCompletedOnboarding) {
    return false;
  }

  useRatingPromptStore.getState().syncSessionUser(userState.firebaseUser.uid);
  return true;
};

const requestNativeReviewOrFallback = async (trigger: RatingPromptTrigger): Promise<boolean> => {
  const storeReview = await loadStoreReviewModule();
  try {
    if (storeReview) {
      const hasAction = await storeReview.hasAction();
      if (hasAction) {
        await logAnalyticsEvent('rating_prompt_attempted', { trigger, path: 'native' });
        await storeReview.requestReview();
        return true;
      }
    }
  } catch {
    // fallback path below
  }

  const openedFallback = await openStoreListing();
  await logAnalyticsEvent('rating_prompt_fallback_opened', {
    trigger,
    platform: Platform.OS === 'ios' ? 'ios' : 'android',
    opened: openedFallback,
  });
  if (openedFallback) {
    await logAnalyticsEvent('rating_prompt_attempted', { trigger, path: 'fallback' });
  }
  return openedFallback;
};

const delay = async (ms: number) => {
  await new Promise((resolve) => setTimeout(resolve, ms));
};

const showTwoStepPrompt = async (
  trigger: RatingPromptTrigger,
  policy: RatingPromptPolicy,
  state: RatingPromptState,
  now: number,
  options?: RatingPromptAttemptOptions,
): Promise<boolean> => {
  const nextBaseState = withPromptRecorded(state, now, options?.tripId);
  await writeState(nextBaseState);
  await logAnalyticsEvent('rating_prompt_shown', {
    trigger,
    source: options?.source,
    tripId: options?.tripId,
    bookingsCount: options?.bookingsCount,
  });

  return new Promise((resolve) => {
    useRatingPromptStore.getState().openPrompt();

    Alert.alert('Enjoying Tripvio?', 'A quick rating helps other travelers discover Tripvio.', [
      {
        text: 'Not now',
        style: 'cancel',
        onPress: () => {
          void logAnalyticsEvent('rating_prompt_dismissed', { trigger, action: 'not_now' });
          void writeState({
            ...nextBaseState,
            deferUntil: now + policy.deferMs,
          });
          useRatingPromptStore.getState().closePrompt();
          resolve(false);
        },
      },
      {
        text: 'Send feedback',
        onPress: () => {
          void logAnalyticsEvent('feedback_entry_tapped', {
            source: 'rating_prompt',
            kind: 'unhappy_path',
          });
          void openSupportMailbox('feedback');
          void writeState({
            ...nextBaseState,
            deferUntil: now + policy.deferMs,
          });
          useRatingPromptStore.getState().closePrompt();
          resolve(false);
        },
      },
      {
        text: 'Rate now',
        onPress: () => {
          void (async () => {
            const handoffSucceeded = await requestNativeReviewOrFallback(trigger);
            const completionState: RatingPromptState = {
              ...nextBaseState,
              completedAt: handoffSucceeded ? Date.now() : null,
              deferUntil: handoffSucceeded ? null : now + policy.deferMs,
            };
            await writeState(completionState);
            useRatingPromptStore.getState().closePrompt();
            resolve(handoffSucceeded);
          })();
        },
      },
    ]);
  });
};

export const maybePromptForRating = async (options: RatingPromptAttemptOptions): Promise<boolean> => {
  if (attemptInFlight) {
    debugRatingPrompt('skip: attempt already in flight', { trigger: options.trigger });
    return false;
  }
  if (!canAttemptPromptNow(options)) {
    debugRatingPrompt('skip: cannot attempt now', {
      trigger: options.trigger,
      hasBlockingUi: options.hasBlockingUi ?? false,
    });
    return false;
  }
  if (!acquirePromptLock('store_review')) {
    debugRatingPrompt('skip: prompt lock busy', { trigger: options.trigger });
    return false;
  }

  try {
    const policy = await getPolicy();
    if (!policy.enabled) {
      debugRatingPrompt('skip: policy disabled', { trigger: options.trigger });
      return false;
    }

    const now = Date.now();
    const persisted = await readState();
    const eligibility = evaluateEligibility({
      now,
      trigger: options.trigger,
      tripId: options.tripId,
      state: persisted,
      policy,
    });
    if (!eligibility.eligible) {
      debugRatingPrompt('skip: ineligible', {
        trigger: options.trigger,
        reason: eligibility.reason,
      });
      return false;
    }

    debugRatingPrompt('eligible: prompt can be shown', {
      trigger: options.trigger,
      tripId: options.tripId,
      source: options.source,
    });

    attemptInFlight = true;
    useRatingPromptStore.getState().markSessionAttempted();
    try {
      if (options.deferMs && options.deferMs > 0) {
        await delay(options.deferMs);
        if (!canAttemptPromptNow(options)) {
          debugRatingPrompt('skip after defer: cannot attempt now', {
            trigger: options.trigger,
            deferMs: options.deferMs,
          });
          return false;
        }
      }
      const shown = await showTwoStepPrompt(options.trigger, policy, persisted, now, options);
      debugRatingPrompt('attempted: prompt flow completed', {
        trigger: options.trigger,
        shown,
      });
      return shown;
    } finally {
      attemptInFlight = false;
    }
  } finally {
    releasePromptLock('store_review');
  }
};

export const openUserInitiatedRating = async (
  source: 'settings' | 'positive_moment' = 'settings',
): Promise<boolean> => {
  if (Platform.OS === 'web') {
    await logAnalyticsEvent('rating_entry_tapped', { source, path: 'unavailable' });
    return false;
  }

  const storeReview = await loadStoreReviewModule();
  try {
    if (storeReview && (await storeReview.hasAction())) {
      await logAnalyticsEvent('rating_entry_tapped', { source, path: 'native' });
      await storeReview.requestReview();
      return true;
    }
  } catch {
    // Continue to store listing fallback.
  }

  const opened = await openStoreListing();
  await logAnalyticsEvent('rating_entry_tapped', {
    source,
    path: opened ? 'store_listing' : 'unavailable',
  });
  return opened;
};

export const __ratingPromptInternals = {
  evaluateEligibility,
  sanitizeState,
  toPolicy,
};
